/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import { Container, Typography, Box, Grid, CircularProgress, Chip } from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface VideoItem {
  id: string;
  title: string;
  videoUrl: string;
  description: string;
  isShort?: boolean;
}

interface VideosContent {
  title: string;
  description: string;
  videos: VideoItem[];
}

export default function GalleryVideos() {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<VideosContent>({
    title: 'Temple Videos',
    description: 'Watch temple videos',
    videos: [
      { id: '1', title: 'Temple Morning Ritual', videoUrl: 'https://www.youtube.com/embed/bFHz3KocQc0', description: 'A view of the morning rituals at the temple.' },
      { id: '2', title: 'Festival Celebrations', videoUrl: 'https://www.youtube.com/embed/bFHz3KocQc0', description: 'Highlights from several festivals celebrated at the temple.' },
      { id: '3', title: 'Temple Tour', videoUrl: 'https://www.youtube.com/embed/bFHz3KocQc0', description: 'A guided tour of the temple premises.' },
      { id: '4', title: 'Evening Pooja', videoUrl: 'https://www.youtube.com/embed/bFHz3KocQc0', description: 'The evening prayer session at the temple.' },
    ]
  });

  useEffect(() => {
    try {
      const unsubscribe = onSnapshot(
        doc(db, 'gallery_content', 'videos'),
        (docSnap) => {
          if (docSnap.exists()) {
            setContent(docSnap.data() as VideosContent);
          }
          setLoading(false);
        },
        (error) => {
          console.error('Error loading videos:', error);
          setLoading(false);
        }
      );
      return () => unsubscribe();
    } catch (error) {
      console.error('Error setting up videos listener:', error);
      setLoading(false);
    }
  }, []);

  // Convert any YouTube URL to embed format
  const toEmbedUrl = (url: string): { embedUrl: string; isShort: boolean } => {
    let videoId = '';
    let isShort = false;
    const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    const shortsMatch = url.match(/\/shorts\/([a-zA-Z0-9_-]{11})/);
    if (watchMatch) videoId = watchMatch[1];
    else if (shortMatch) videoId = shortMatch[1];
    else if (shortsMatch) { videoId = shortsMatch[1]; isShort = true; }
    if (videoId) return { embedUrl: `https://www.youtube.com/embed/${videoId}`, isShort };
    return { embedUrl: url, isShort: false };
  };

  const videos = content.videos.map(v => {
    const { embedUrl, isShort } = toEmbedUrl(v.videoUrl);
    return { ...v, videoUrl: embedUrl, isShort: v.isShort ?? isShort };
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO title="Video Gallery — Kottayil Temple | വീഡിയോകൾ" description="Watch videos of rituals, festivals, and ceremonies at Kottayil Temple (Kotta-Kovilakam Kshethram), Muchukunnu, Kerala." path="/gallery/videos" keywords="Kottayil temple videos, ritual videos, festival videos, temple video gallery, Muchukunnu temple videos, കൊട്ടയിൽ ക്ഷേത്രം വീഡിയോ" />
      <PageBanner title="Video Gallery" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{ borderRadius: '24px', overflow: 'hidden', mb: 3.5, boxShadow: '0 20px 60px rgba(239,68,68,0.15)' }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #7f1d1d 0%, #ef4444 55%, #fca5a5 100%)',
                px: { xs: 3, sm: 4 }, pt: { xs: 4, sm: 5 }, pb: { xs: 5, sm: 6 },
                position: 'relative', overflow: 'hidden',
              }}>
                <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'relative', zIndex: 2 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5,
                  }}>
                    <VideocamIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 900, color: '#fff', fontSize: { xs: '1.8rem', sm: '2.4rem' }, mb: 1.5, lineHeight: 1.1 }}>
                    Video Gallery
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: { xs: '0.9rem', sm: '1rem' }, lineHeight: 1.7, maxWidth: 500, mb: 2.5 }}>
                    Watch sacred ceremonies, festival celebrations, and explore the beauty of our temple through video.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip icon={<VideocamIcon sx={{ fontSize: 16, color: '#ef4444 !important' }} />} label="Videos" size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#ef4444', fontWeight: 700, fontSize: '0.7rem', height: 28, borderRadius: '14px' }} />
                    <Chip icon={<PlayCircleOutlineIcon sx={{ fontSize: 16, color: '#fff !important' }} />} label={`${videos.length} Videos`} size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, fontSize: '0.7rem', height: 28, borderRadius: '14px', backdropFilter: 'blur(10px)' }} />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* VIDEO CARDS */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {videos.map((video, index) => {
                const vGradients: [string, string][] = [
                  ['#ef4444', '#dc2626'], ['#8b5cf6', '#7c3aed'],
                  ['#f59e0b', '#d97706'], ['#10b981', '#059669'],
                  ['#3b82f6', '#2563eb'], ['#ec4899', '#db2777'],
                ];
                const gr = vGradients[index % vGradients.length];
                return (
                  <Box key={index} sx={{
                    bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                    transition: 'all 350ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 48px rgba(0,0,0,0.08)' },
                  }}>
                    {/* Gradient header */}
                    <Box sx={{
                      background: `linear-gradient(135deg, ${gr[0]}, ${gr[1]})`,
                      px: { xs: 2, sm: 2.5 }, py: 1.5,
                      display: 'flex', alignItems: 'center', gap: 1.5,
                    }}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: '10px',
                        background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
                        border: '1.5px solid rgba(255,255,255,0.3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <PlayCircleOutlineIcon sx={{ fontSize: 16, color: '#fff' }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff', flex: 1 }}>
                        {video.title}
                      </Typography>
                      <Box sx={{ px: 1.2, py: 0.3, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)' }}>
                        <Typography sx={{ fontSize: '0.6rem', color: '#fff', fontWeight: 700 }}>
                          VIDEO {index + 1}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Iframe */}
                    <Box sx={{
                      position: 'relative',
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      bgcolor: video.isShort ? '#000' : 'transparent',
                    }}>
                      <Box sx={{
                        position: 'relative',
                        width: video.isShort ? { xs: '55%', sm: '45%', md: '40%' } : '100%',
                        paddingTop: video.isShort ? { xs: '97.8%', sm: '80%', md: '71.1%' } : '56.25%',
                      }}>
                        <iframe
                          src={video.videoUrl}
                          title={video.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                        />
                      </Box>
                    </Box>

                    {/* Description */}
                    {video.description && (
                      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5 }}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#888', lineHeight: 1.7 }}>
                          {video.description}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* FOOTER */}
            <Box sx={{
              mt: 3.5, borderRadius: '24px', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              boxShadow: '0 8px 32px rgba(15,23,42,0.3)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ px: { xs: 3, sm: 3.5 }, py: 3, position: 'relative', zIndex: 2, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    background: `linear-gradient(135deg, ${GOLD}, #f59e0b)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <YouTubeIcon sx={{ fontSize: 22, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>Subscribe to Our YouTube Channel</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, maxWidth: 500, mx: 'auto' }}>
                  Stay updated with the latest temple videos, live streams, and event highlights.
                </Typography>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="gallery" activePath="/gallery/videos" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
